import { useEffect, useState } from 'react';
import { useSalaryMutations, useSalarySettings, type SalarySettings } from '@/hooks/queries';
import type { DeficitLeaveBand } from '@chronos/shared';
import { Card, ErrorNote, PageHeader, Spinner, SubmitButton } from '@/components/ui';

/** Admin-only. The numbers behind the salary sheet. */
export function SalarySettingsPage() {
  const { data, isLoading, error } = useSalarySettings();
  const { updateSettings } = useSalaryMutations();
  const [form, setForm] = useState<SalarySettings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading || !form) return <div className="flex justify-center py-16"><Spinner /></div>;

  const set = <K extends keyof SalarySettings>(key: K, value: SalarySettings[K]) => setForm({ ...form, [key]: value });
  const money = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: form.currency || 'INR', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Salary settings" description="Bonus tiers, paid leave and how half days count. Changes re-compute every month's sheet." />
      <ErrorNote error={error ?? updateSettings.error} />

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSaved(false);
          updateSettings.mutate(form, { onSuccess: () => setSaved(true) });
        }}
      >
        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Attendance bonus</h2>
            <p className="mt-0.5 text-xs text-slate-500">Paid on top of salary, decided by the month's leave units (leaves + half days × weight + adjustments).</p>
          </div>
          <Field label="No leaves" hint="0 units in the month.">
            <MoneyInput value={form.attendanceBonusNoLeave} onChange={(v) => set('attendanceBonusNoLeave', v)} currency={form.currency} />
          </Field>
          <Field label="One leave" hint="More than 0, up to 1 unit.">
            <MoneyInput value={form.attendanceBonusOneLeave} onChange={(v) => set('attendanceBonusOneLeave', v)} currency={form.currency} />
          </Field>
          <Field label="Two or more leaves" hint="More than 1 unit.">
            <MoneyInput value={form.attendanceBonusTwoPlusLeaves} onChange={(v) => set('attendanceBonusTwoPlusLeaves', v)} currency={form.currency} />
          </Field>
        </Card>

        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Leave and deductions</h2>
          </div>
          <Field label="Paid leaves per month" hint="Accrue into each employee's leave bank every month from the start of the leave year, or from their joining month if they joined later. Unused days carry forward until the year-end month, when the bank restarts.">
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={31} step={0.5} className="input max-w-[6rem]" value={form.paidLeavesPerMonth} onChange={(e) => set('paidLeavesPerMonth', Number(e.target.value))} />
              <span className="text-sm text-slate-500">day(s)</span>
            </div>
          </Field>
          <Field label="Half day counts as" hint="For both deductions and the bonus tier. 0.5 is the usual choice.">
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={1} step={0.25} className="input max-w-[6rem]" value={form.halfDayLeaveWeight} onChange={(e) => set('halfDayLeaveWeight', Number(e.target.value))} />
              <span className="text-sm text-slate-500">of a leave</span>
            </div>
          </Field>
          <Field label="Leave year ends in" hint="The remaining bank balance is settled in this month's sheet; the bank restarts from zero the month after.">
            <select className="input max-w-[10rem]" value={form.leaveYearEndMonth} onChange={(e) => set('leaveYearEndMonth', Number(e.target.value))}>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((name, i) => (
                <option key={name} value={i + 1}>{name}</option>
              ))}
            </select>
          </Field>
          <Field label="Pay out unused leave at year end" hint="On: closing balance × per-day rate is added to that month's net pay. Off: the balance is simply forfeited.">
            <button
              type="button"
              role="switch"
              aria-checked={form.encashLeavesAtYearEnd}
              onClick={() => set('encashLeavesAtYearEnd', !form.encashLeavesAtYearEnd)}
              className={`relative h-6 w-11 rounded-full transition ${form.encashLeavesAtYearEnd ? 'bg-brand-600' : 'bg-slate-300'}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${form.encashLeavesAtYearEnd ? 'left-[1.375rem]' : 'left-0.5'}`} />
            </button>
          </Field>
          <Field label="Currency" hint="ISO code, display only.">
            <input className="input max-w-[6rem] uppercase" maxLength={3} value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
          </Field>
        </Card>

        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Monthly hours deficit</h2>
            <p className="mt-1 text-xs text-slate-500">
              Short hours on full days, minus hours worked beyond the day, netted across the month. Leave charged here is
              deducted from pay and counts toward the bonus tier, but is never taken from the paid leave bank. A deficit below
              the first row costs nothing, so that row is the monthly allowance. No rows means the feature is off.
            </p>
          </div>
          <div className="px-5 py-4">
            <DeficitBands
              bands={form.deficitLeaveBands ?? []}
              onChange={(bands) => set('deficitLeaveBands', bands)}
            />
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold text-slate-800">Worked example</h2>
          <p className="mt-1 text-xs text-slate-500">Salary {money(44000)}, 22 working days → {money(2000)} per day, with {form.paidLeavesPerMonth} day(s) in the bank this month and nothing carried in.</p>
          <table className="mt-3 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500"><th className="py-1">Leave units</th><th className="py-1 text-right">Deduction</th><th className="py-1 text-right">Bonus</th><th className="py-1 text-right">Net</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {[0, 0.5, 1, 1.5, 2, 3].map((u) => {
                const unpaid = Math.max(0, u - form.paidLeavesPerMonth);
                const ded = unpaid * 2000;
                const bonus = u <= 0 ? form.attendanceBonusNoLeave : u <= 1 ? form.attendanceBonusOneLeave : form.attendanceBonusTwoPlusLeaves;
                return (
                  <tr key={u}>
                    <td className="py-1 tabular-nums">{u}</td>
                    <td className={`py-1 text-right font-mono tabular-nums ${ded ? 'text-rose-700' : 'text-slate-400'}`}>{ded ? `−${money(ded)}` : '—'}</td>
                    <td className={`py-1 text-right font-mono tabular-nums ${bonus ? 'text-emerald-700' : 'text-slate-400'}`}>{bonus ? `+${money(bonus)}` : '—'}</td>
                    <td className="py-1 text-right font-mono font-semibold tabular-nums">{money(44000 - ded + bonus)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        <div className="flex items-center gap-3">
          <SubmitButton pending={updateSettings.isPending} className="btn-primary">Save settings</SubmitButton>
          {saved && !updateSettings.isPending && <span className="text-sm text-emerald-700">Saved.</span>}
        </div>
      </form>
    </div>
  );
}

function MoneyInput({ value, onChange, currency }: { value: number; onChange: (v: number) => void; currency: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500">{currency}</span>
      <input type="number" min={0} step={100} className="input max-w-[8rem]" value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

/**
 * The deficit ladder. Rows are kept in entry order while editing — sorting as
 * the admin types would move the row under their cursor — and the resolver on
 * the server is order-independent, so the stored order does not matter.
 */
function DeficitBands({
  bands,
  onChange,
}: {
  bands: DeficitLeaveBand[];
  onChange: (bands: DeficitLeaveBand[]) => void;
}) {
  const update = (i: number, patch: Partial<DeficitLeaveBand>) =>
    onChange(bands.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  return (
    <div className="space-y-2">
      {bands.length === 0 && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          No bands: a deficit never becomes leave. Add one to start charging.
        </p>
      )}

      {bands.map((band, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="w-16 shrink-0 text-slate-500">{i === 0 ? 'From' : 'then from'}</span>
          <input
            type="number" min={0.25} max={500} step={0.25} className="input max-w-[6rem]"
            value={band.fromHours}
            onChange={(e) => update(i, { fromHours: Number(e.target.value) })}
          />
          <span className="text-slate-500">hours short →</span>
          <input
            type="number" min={0} max={31} step={0.5} className="input max-w-[6rem]"
            value={band.days}
            onChange={(e) => update(i, { days: Number(e.target.value) })}
          />
          <span className="text-slate-500">day(s) leave</span>
          <button
            type="button"
            className="btn-ghost px-2 py-1 text-xs text-rose-600"
            onClick={() => onChange(bands.filter((_, j) => j !== i))}
          >
            Remove
          </button>
        </div>
      ))}

      <button
        type="button"
        className="btn-secondary py-1 text-xs"
        onClick={() => onChange([...bands, { fromHours: bands.length ? 0 : 4, days: 0.5 }])}
      >
        Add band
      </button>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}
